import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApi } from '../lib/api'
import { useUser, canAccess } from '../lib/auth'
import type { Invoice, InvoiceStatus, Order, OrderStatus } from '../types'
import {
  STATUS_LABELS,
  ARCHIVED_ORDER_STATUSES,
  INVOICE_STATUS_LABELS,
  ARCHIVED_INVOICE_STATUSES,
} from '../types'
import { FlagThumbnail } from '../components/FlagThumbnail'

type InvoiceRow = Invoice & { order_reference?: string }

const PAGE_SIZE = 20

export default function ArchivePage() {
  const { user } = useUser()
  const canSeeInvoices = !user || canAccess(user, 'invoices')

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Archiv</h1>
      <ArchivedOrders />
      {canSeeInvoices && (
        <>
          <h2 style={{ marginTop: 40, marginBottom: 16 }}>Rechnungen</h2>
          <ArchivedInvoices />
        </>
      )}
    </div>
  )
}

function ArchivedOrders() {
  const { apiFetch } = useApi()
  const navigate = useNavigate()
  const [orders, setOrders] = useState<Order[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String((page - 1) * PAGE_SIZE),
      archived: 'true',
    })
    if (statusFilter) params.set('status', statusFilter)
    if (search) params.set('q', search)

    apiFetch<{ orders: Order[]; total: number }>(`/api/orders?${params}`)
      .then(({ orders: o, total: t }) => {
        setOrders(o)
        setTotal(t)
      })
      .finally(() => setLoading(false))
  }, [page, statusFilter, search]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Aufträge</h2>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Referenz, Kunde suchen…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          style={{ width: 300 }}
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as OrderStatus | ''); setPage(1) }}
          style={{ width: 180 }}
        >
          <option value="">Alle</option>
          {ARCHIVED_ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Aufträge werden geladen…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Referenz</th>
                <th>Kunde</th>
                <th>Flaggen</th>
                <th>Status</th>
                <th>Aktualisiert</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>Keine archivierten Aufträge gefunden</td></tr>
              )}
              {orders.map((o) => (
                <tr
                  key={o.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/orders/${o.id}`)}
                >
                  <td><Link to={`/orders/${o.id}`} onClick={(e) => e.stopPropagation()}>{o.reference}</Link></td>
                  <td>{o.client_name}<br /><small style={{ color: 'var(--color-muted)' }}>{o.client_email}</small></td>
                  <td><FlagStrip flags={o.flags} /></td>
                  <td><span className={`badge badge-${o.status}`}>{STATUS_LABELS[o.status]}</span></td>
                  <td>{new Date(o.updated_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Zurück</button>
          <span style={{ alignSelf: 'center', color: 'var(--color-muted)' }}>Seite {page} / {totalPages}</span>
          <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Weiter →</button>
        </div>
      )}
    </div>
  )
}

function ArchivedInvoices() {
  const { apiFetch } = useApi()
  const [invoices, setInvoices] = useState<InvoiceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all')

  useEffect(() => {
    apiFetch<{ invoices: InvoiceRow[] }>('/api/invoices')
      .then(({ invoices: inv }) => setInvoices(inv))
      .finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (!ARCHIVED_INVOICE_STATUSES.includes(inv.status)) return false
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false
      if (search) {
        const q = search.toLowerCase()
        if (
          !inv.invoice_number.toLowerCase().includes(q) &&
          !(inv.order_reference ?? '').toLowerCase().includes(q)
        ) return false
      }
      return true
    })
  }, [invoices, search, statusFilter])

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Rechnungsnummer oder Auftragsreferenz suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'all')}
          style={{ width: 160 }}
        >
          <option value="all">Alle</option>
          {ARCHIVED_INVOICE_STATUSES.map((s) => (
            <option key={s} value={s}>{INVOICE_STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Rechnungen werden geladen…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Rechnung #</th>
                <th>Auftrag</th>
                <th>Betrag</th>
                <th>Erstellt</th>
                <th>Status</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                    Keine archivierten Rechnungen gefunden
                  </td>
                </tr>
              )}
              {filtered.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.invoice_number}</td>
                  <td>
                    <Link to={`/orders/${inv.order_id}`}>
                      {inv.order_reference ?? `Auftrag #${inv.order_id}`}
                    </Link>
                  </td>
                  <td>${inv.total_amount.toFixed(2)}</td>
                  <td>{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className={`badge badge-${inv.status}`}>
                      {INVOICE_STATUS_LABELS[inv.status]}
                    </span>
                  </td>
                  <td>
                    <a
                      href={`/api/invoices/${inv.id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 12 }}
                    >
                      Herunterladen
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const MAX_SHOWN = 4

function FlagStrip({ flags }: { flags: Order['flags'] }) {
  if (flags.length === 0) {
    return <span style={{ color: 'var(--color-muted)', fontSize: 13 }}>—</span>
  }
  const shown = flags.slice(0, MAX_SHOWN)
  const overflow = flags.length - MAX_SHOWN

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {shown.map((f) => (
        <div key={f.id} title={f.name + (f.type === 'custom' ? ' (Eigene)' : '')}>
          <FlagThumbnail id={f.id} type={f.type} imageUrl={f.image_url} name={f.name} width={36} height={22} />
        </div>
      ))}
      {overflow > 0 && (
        <span style={{ fontSize: 12, color: 'var(--color-muted)', marginLeft: 2 }}>+{overflow}</span>
      )}
    </div>
  )
}
